import { useMemo, useState } from 'react';
import { Alert, Button, Checkbox, Flex, Form, Input, Modal, Segmented, Select, Table, Tag, Typography } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../app/api-context';
import { CATEGORY_LABEL, PURPOSE_LABEL, type VocabItem, type VocabPurpose } from '../../lib/vocab-types';

/** POST /vocab/create 항목(functions/src/vocab/create.ts) */
export interface CreateItemInput {
  text: string;
  category: string;
  vocabType: 'core' | 'fringe';
  purpose: VocabPurpose;
  slug: string | null;
  keywords: string[];
  rationale: string;
  imageSubject?: string | null;
  allowSameText?: boolean;
}

const SLUG = /^[a-z][a-z0-9_]*[a-z0-9]$/;

/** 슬러그 형식 오류 사유(없으면 null) */
export function slugProblem(slug: string | null): string | null {
  if (!slug) return null;
  if (!SLUG.test(slug)) return '슬러그는 영문 소문자로 시작하고 영문 소문자·숫자·밑줄만 씁니다.';
  if (/_\d+$/.test(slug)) return '슬러그는 숫자 번호(_01 등)로 끝날 수 없습니다.';
  return null;
}

export interface ParsedLine {
  line: number;
  text: string;
  slug: string | null;
  keywords: string[];
  problem: string | null;
  /** 같은 표기의 관리 중 어휘 ID */
  sameText: string[];
}

/**
 * 여러 줄 붙여넣기: 줄마다 `표기, 슬러그, 키워드1;키워드2`(슬러그·키워드는 생략 가능).
 * 탭으로 구분해도 된다(스프레드시트에서 복사). 빈 줄은 건너뛴다.
 */
export function parseLines(src: string, existing: readonly Pick<VocabItem, 'vocabId' | 'text' | 'slug'>[]): ParsedLine[] {
  const out: ParsedLine[] = [];
  const seenText = new Map<string, number>();
  const seenSlug = new Map<string, number>();
  src.split(/\r?\n/).forEach((raw, i) => {
    if (!raw.trim()) return;
    const [text = '', slugRaw = '', kwRaw = ''] = raw.split(/\t|,/).map((c) => c.trim());
    const slug = slugRaw || null;
    const keywords = kwRaw.split(';').map((k) => k.trim()).filter(Boolean);
    const sameText = existing.filter((v) => v.text === text).map((v) => v.vocabId);
    let problem: string | null = null;
    if (!text) problem = '표기가 없습니다.';
    else if (text.length > 30) problem = '표기는 30자 이하입니다.';
    else problem = slugProblem(slug);
    if (!problem && seenText.has(text)) problem = `${seenText.get(text)}번째 줄과 표기가 같습니다.`;
    if (!problem && slug && seenSlug.has(slug)) problem = `${seenSlug.get(slug)}번째 줄과 슬러그가 같습니다.`;
    if (!problem && slug) {
      const owner = existing.find((v) => v.slug === slug);
      if (owner) problem = `슬러그 ${slug}는 ${owner.vocabId}가 이미 씁니다.`;
    }
    if (!problem && sameText.length) problem = `같은 표기 어휘가 있습니다(${sameText.join(', ')}). 다른 뜻이면 '한 개' 추가에서 동음이의어로 저장해 주세요.`;
    seenText.set(text, i + 1);
    if (slug) seenSlug.set(slug, i + 1);
    out.push({ line: i + 1, text, slug, keywords, problem, sameText });
  });
  return out;
}

type Mode = 'one' | 'many';

interface CommonValues {
  category: string;
  vocabType: 'core' | 'fringe';
  purpose: VocabPurpose;
  rationale: string;
}

interface OneValues extends CommonValues {
  text: string;
  slug: string;
  keywords: string[];
  imageSubject: string;
  allowSameText: boolean;
}

const purposeOptions = (Object.keys(PURPOSE_LABEL) as VocabPurpose[]).map((p) => ({ value: p, label: PURPOSE_LABEL[p].label }));

function CommonFields() {
  return (
    <>
      <Form.Item name="category" label="카테고리" rules={[{ required: true, message: '카테고리를 골라 주세요.' }]}>
        <Select placeholder="카테고리" options={Object.entries(CATEGORY_LABEL).map(([value, label]) => ({ value, label }))} />
      </Form.Item>
      <Flex gap={24} wrap>
        <Form.Item name="vocabType" label="핵심/주변">
          <Segmented options={[{ value: 'core', label: '핵심' }, { value: 'fringe', label: '주변' }]} />
        </Form.Item>
        <Form.Item name="purpose" label="용도" extra="픽사베이에 없는 한국 사물·음식처럼 검색용으로만 제공할 어휘는 '검색 이미지 전용'">
          <Segmented options={purposeOptions} />
        </Form.Item>
      </Flex>
    </>
  );
}

/** 어휘 직접 추가(#98, D-V9). 한 개 입력 또는 여러 줄 붙여넣기 */
export function VocabCreateModal({ open, existing, onClose, onCreated }: {
  open: boolean;
  existing: readonly VocabItem[];
  onClose: () => void;
  /** 저장 후 이미지 생성 요청으로 이어갈 때 generate=true */
  onCreated: (ids: string[], generate: boolean) => void;
}) {
  const api = useApi();
  const qc = useQueryClient();
  const [mode, setMode] = useState<Mode>('one');
  const [form] = Form.useForm<OneValues>();
  const [lines, setLines] = useState('');
  const text = Form.useWatch('text', form) ?? '';
  const sameText = useMemo(() => existing.filter((v) => v.text === text.trim()), [existing, text]);
  const parsed = useMemo(() => parseLines(lines, existing), [lines, existing]);
  const bad = parsed.filter((p) => p.problem);

  const save = useMutation({
    mutationFn: (items: CreateItemInput[]) => api.post<{ created: number; ids: string[] }>('/vocab/create', { items }),
  });

  const reset = () => {
    form.resetFields();
    setLines('');
    save.reset();
  };
  const close = () => {
    reset();
    onClose();
  };

  const submit = async (generate: boolean) => {
    let values: OneValues;
    try {
      values = await form.validateFields(mode === 'one' ? undefined : ['category', 'vocabType', 'purpose', 'rationale']);
    } catch {
      return;
    }
    const common = { category: values.category, vocabType: values.vocabType, purpose: values.purpose, rationale: (values.rationale ?? '').trim() };
    const items: CreateItemInput[] = mode === 'one'
      ? [{ ...common, text: values.text.trim(), slug: values.slug?.trim() || null, keywords: values.keywords ?? [], imageSubject: values.imageSubject?.trim() || null, allowSameText: !!values.allowSameText }]
      : parsed.map((p) => ({ ...common, text: p.text, slug: p.slug, keywords: p.keywords }));
    if (!items.length) return;
    const r = await save.mutateAsync(items).catch(() => null);
    if (!r) return;
    await qc.invalidateQueries({ queryKey: ['vocab'] });
    reset();
    onCreated(r.ids, generate);
  };

  const canSave = mode === 'one' || (parsed.length > 0 && bad.length === 0 && parsed.length <= 100);

  return (
    <Modal open={open} title="어휘 추가" onCancel={close} width={720} footer={null}>
      <Typography.Paragraph type="secondary">
        후보 목록에 없는 어휘를 관리 어휘로 저장합니다. 출처는 '기획자 추가'로 기록합니다. 표기는 해요체·표준어 규칙(D-V1~D-V3)을 따라 주세요.
      </Typography.Paragraph>
      <Segmented<Mode> value={mode} onChange={setMode} options={[{ value: 'one', label: '한 개' }, { value: 'many', label: '여러 개 붙여넣기' }]} style={{ marginBottom: 16 }} />
      <Form form={form} layout="vertical" requiredMark={false} initialValues={{ vocabType: 'fringe', purpose: 'default_card', keywords: [], allowSameText: false }}>
        {mode === 'one' ? (
          <>
            <Form.Item name="text" label="표기" rules={[{ required: true, whitespace: true, message: '표기를 입력해 주세요.' }, { max: 30, message: '30자 이하로 입력해 주세요.' }, { pattern: /^[^,"]*$/, message: '쉼표·큰따옴표는 쓸 수 없습니다.' }]}>
              <Input placeholder="예: 떡볶이" />
            </Form.Item>
            {sameText.length > 0 && (
              <Form.Item name="allowSameText" valuePropName="checked" extra={`같은 표기: ${sameText.map((v) => `${v.vocabId}${v.slug ? ` · ${v.slug}` : ' · 슬러그 없음'}`).join(', ')}. 동음이의어는 모두 슬러그가 있어야 합니다.`}>
                <Checkbox>같은 표기 어휘가 있지만 다른 뜻(동음이의어)으로 저장</Checkbox>
              </Form.Item>
            )}
            <CommonFields />
            <Form.Item
              name="slug"
              label="영문 파일명(슬러그)"
              extra="검색 이미지 파일명 기준입니다(게시 전까지 비워 둘 수 있음). 예: tteokbokki → images/food_object/tteokbokki_01.webp"
              rules={[{ validator: (_, v: string | undefined) => { const p = slugProblem(v?.trim() || null); return p ? Promise.reject(new Error(p)) : Promise.resolve(); } }]}
            >
              <Input placeholder="예: tteokbokki" />
            </Form.Item>
            <Form.Item name="keywords" label="동의어·검색 키워드" extra="입력 후 Enter.">
              <Select mode="tags" tokenSeparators={[',']} open={false} placeholder="예: 분식" />
            </Form.Item>
            <Form.Item name="imageSubject" label="생성 피사체 설명(선택)" rules={[{ pattern: /^[^"]*$/, message: '큰따옴표(")는 쓸 수 없습니다.' }, { max: 200, message: '200자 이하로 입력해 주세요.' }]}>
              <Input placeholder="예: 접시에 담긴 떡볶이 한 그릇" />
            </Form.Item>
          </>
        ) : (
          <>
            <CommonFields />
            <Form.Item label="어휘 목록" extra="줄마다 `표기, 슬러그, 키워드1;키워드2`. 슬러그·키워드는 생략할 수 있고, 스프레드시트에서 복사한 탭 구분도 됩니다. 위 카테고리·구분·용도가 모두에 적용됩니다. 한 번에 100개까지.">
              <Input.TextArea aria-label="어휘 목록" value={lines} onChange={(e) => setLines(e.target.value)} autoSize={{ minRows: 4, maxRows: 10 }} placeholder={'떡볶이, tteokbokki, 분식\n붕어빵, bungeoppang\n김밥'} />
            </Form.Item>
            {parsed.length > 0 && (
              <Table<ParsedLine>
                size="small"
                rowKey="line"
                pagination={false}
                scroll={{ y: 240 }}
                dataSource={parsed}
                style={{ marginBottom: 12 }}
                columns={[
                  { title: '줄', dataIndex: 'line', width: 48 },
                  { title: '표기', dataIndex: 'text' },
                  { title: '슬러그', dataIndex: 'slug', render: (s: string | null) => (s ? <Typography.Text code>{s}</Typography.Text> : <Typography.Text type="secondary">없음</Typography.Text>) },
                  { title: '키워드', dataIndex: 'keywords', render: (k: string[]) => k.map((x) => <Tag key={x}>{x}</Tag>) },
                  { title: '확인', dataIndex: 'problem', render: (p: string | null) => (p ? <Typography.Text type="danger">{p}</Typography.Text> : <Tag color="success">추가 가능</Tag>) },
                ]}
              />
            )}
            {parsed.length > 100 && <Alert type="warning" showIcon message="한 번에 100개까지 추가할 수 있습니다." style={{ marginBottom: 12 }} />}
          </>
        )}
        <Form.Item name="rationale" label="추가 사유(선택)" rules={[{ max: 2000 }]}>
          <Input.TextArea autoSize={{ minRows: 1, maxRows: 4 }} placeholder="예: 픽사베이에 없는 한국 분식" />
        </Form.Item>
      </Form>
      {save.isError && <Alert type="error" showIcon message="저장하지 못했습니다" description={(save.error as Error).message} style={{ marginBottom: 12 }} />}
      <Flex justify="flex-end" gap={8}>
        <Button onClick={close}>취소</Button>
        <Button disabled={!canSave} loading={save.isPending} onClick={() => submit(false)}>저장</Button>
        <Button type="primary" disabled={!canSave} loading={save.isPending} onClick={() => submit(true)}>저장하고 이미지 생성 요청</Button>
      </Flex>
    </Modal>
  );
}
